import { useCallback, useMemo } from 'react';

import type { Subscription } from '~/interfaces/subscriptions';
import useStaticShows from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useStaticShows';
import { getPodcastHref as resolvePodcastHref } from '~/routes/subscribe/$subscriberId/_utils/getPodcastHref';

import { PodcastsContext, type PodcastsContextType } from './PodcastsContext';
import { usePreflightContext } from './usePreflightContext';

type Props = {
  subscription: Subscription;
  subscriberId: string;
  children: React.ReactNode;
};

/**
 * Owns the Podcasts tab — a thin wrapper over `useStaticShows` that reshapes the shows with the
 * subscription-derived `isActive` flag and exposes the podcast-href builder. Visibility
 * (`hasPodcasts`) is BE-resolved via `PreflightProvider`. Renders no UI; the colocated
 * `PodcastsSection` child renders the `PodcastsElement` from context.
 */
export const PodcastsProvider = ({ subscription, subscriberId, children }: Props) => {
  const { hasPodcasts } = usePreflightContext();

  // Client-fetched (lazily, only when the subscriber has podcasts) so the tab
  // works on every route that renders Page, not just the manage loader.
  const { shows: podcastShows, isLoading: isLoadingPodcasts } = useStaticShows({
    enabled: hasPodcasts,
    subscriberId,
  });

  const getPodcastHref = useCallback((showId: string) => resolvePodcastHref({ showId, subscriberId }), [subscriberId]);

  const isActive = subscription?.status === 'active';
  const shows = useMemo(() => podcastShows.map((show) => ({ ...show, isActive })), [podcastShows, isActive]);

  const value: PodcastsContextType = {
    shows,
    hasPodcasts,
    isLoadingPodcasts,
    getPodcastHref,
  };

  return <PodcastsContext.Provider value={value}>{children}</PodcastsContext.Provider>;
};
